/**
 * Block 6 (2026-05-26): Бухгалтерські константи — read-only viewer.
 *
 * 3 tabs: Поточні / Обчислені / Iсторія (drill-down).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  accountingConstants,
  type ConstantSnapshot,
  type DerivedValue,
} from '@/api/accounting_constants';
import DatePopover from '@/components/DatePopover';


const fmtValue = (v: string, unit: string) => {
  const num = Number(v);
  if (unit === '%') {
    return num.toLocaleString('uk-UA', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }) + ' %';
  }
  const formatted = num.toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${formatted} ${unit}`;
};


const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};


type Tab = 'current' | 'derived' | 'history';


export function AccountingConstantsPage() {
  const [tab, setTab] = useState<Tab>('current');
  const [atDate, setAtDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [historyCode, setHistoryCode] = useState<string | null>(null);

  const currentQ = useQuery({
    queryKey: ['ac-current', atDate],
    queryFn: () => accountingConstants.list(atDate),
  });

  const derivedQ = useQuery({
    queryKey: ['ac-derived', atDate],
    queryFn: () => accountingConstants.derived(atDate),
  });

  const historyQ = useQuery({
    queryKey: ['ac-history', historyCode],
    queryFn: () => accountingConstants.history(historyCode!),
    enabled: !!historyCode,
  });

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Бухгалтерські константи</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Read-only • Platform-managed
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '8px 0' }}>
        <button
          onClick={() => setTab('current')}
          style={{ fontWeight: tab === 'current' ? 'bold' : 'normal' }}
        >
          Поточні
        </button>
        <button
          onClick={() => setTab('derived')}
          style={{ fontWeight: tab === 'derived' ? 'bold' : 'normal' }}
        >
          Обчислені
        </button>
        {historyCode && (
          <button
            onClick={() => setTab('history')}
            style={{ fontWeight: tab === 'history' ? 'bold' : 'normal' }}
          >
            Iсторія: {historyCode}
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          На дату:
          <DatePopover
            value={atDate}
            onChange={(v) => setAtDate(v)}
          />
        </span>
      </div>

      {tab === 'current' && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ padding: 6, textAlign: 'left' }}>Код</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Опис</th>
              <th style={{ padding: 6, textAlign: 'right' }}>Значення</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Дiє з</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Джерело</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {currentQ.data?.map((c: ConstantSnapshot) => (
              <tr key={c.code} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: 6, fontFamily: 'monospace' }}>{c.code}</td>
                <td style={{ padding: 6 }}>{c.description}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>
                  {fmtValue(c.value, c.unit)}
                </td>
                <td style={{ padding: 6 }}>{fmtDate(c.effective_from)}</td>
                <td style={{ padding: 6, fontSize: '0.85em', color: '#555' }}>
                  {c.source}
                </td>
                <td style={{ padding: 6 }}>
                  <button onClick={() => {
                    setHistoryCode(c.code);
                    setTab('history');
                  }}>
                    Iсторія
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'derived' && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ padding: 6, textAlign: 'left' }}>Код</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Опис</th>
              <th style={{ padding: 6, textAlign: 'right' }}>Значення</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Формула</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Категорія</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Джерело</th>
            </tr>
          </thead>
          <tbody>
            {derivedQ.data?.map((d: DerivedValue) => (
              <tr key={d.code} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: 6, fontFamily: 'monospace' }}>{d.code}</td>
                <td style={{ padding: 6 }}>{d.description}</td>
                <td style={{ padding: 6, textAlign: 'right' }}>
                  {fmtValue(d.value, d.unit)}
                </td>
                <td style={{ padding: 6, fontFamily: 'monospace' }}>{d.formula}</td>
                <td style={{ padding: 6, fontSize: '0.85em' }}>
                  {d.category === 'AT_DATE' ? (
                    <span style={{ color: '#080' }}>За датою</span>
                  ) : (
                    <span style={{ color: '#a60', fontWeight: 'bold' }}>
                      Снапшот року
                    </span>
                  )}
                </td>
                <td style={{ padding: 6, fontSize: '0.85em', color: '#555' }}>
                  {d.legal_reference}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'history' && historyCode && (
        <>
          <h3>{historyCode} — Iсторія</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={{ padding: 6, textAlign: 'left' }}>Дiє з</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Значення</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Джерело</th>
              </tr>
            </thead>
            <tbody>
              {historyQ.data?.ranges.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: 6 }}>{fmtDate(r.effective_from)}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    {fmtValue(r.value, r.unit)}
                  </td>
                  <td style={{ padding: 6, fontSize: '0.85em', color: '#555' }}>
                    {r.source}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
